import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, Clock, ArrowLeft, ExternalLink, Heart, Check, Facebook, Instagram, Youtube, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { EventCard } from "@/components/EventCard";
import { CategoryFilter } from "@/components/CategoryFilter";
import { PrayerTimesRow } from "@/components/PrayerTimes";
import { InviteButton } from "@/components/InviteButton";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

export default function MosqueDetail() {
  const { slug, mosqueId } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assoc, setAssoc] = useState(null);
  const [mosque, setMosque] = useState(null);
  const [events, setEvents] = useState([]);
  const [following, setFollowing] = useState(false);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get(`/associations/${slug}`).then(({ data }) => setAssoc(data)).catch(() => {});
    api.get(`/associations/${slug}/mosques/${mosqueId}`).then(({ data }) => setMosque(data)).catch(() => {});
    api.get(`/associations/${slug}/events`, { params: { mosque_id: mosqueId, upcoming: true, limit: 50 } })
      .then(({ data }) => setEvents(data)).catch(() => {});
  }, [slug, mosqueId]);

  useEffect(() => {
    if (user) {
      api.get(`/associations/${slug}/membership`)
        .then(({ data }) => setFollowing(data.followed_mosques.includes(mosqueId)))
        .catch(() => {});
    }
  }, [slug, mosqueId, user]);

  const toggleFollow = async () => {
    if (!user) {
      sessionStorage.setItem("pending_follow", JSON.stringify({ slug, mosqueId, name: mosque.name }));
      return navigate("/register");
    }
    const { data } = await api.post(`/associations/${slug}/mosques/${mosqueId}/follow`);
    setFollowing(data.following);
    setMosque((m) => ({ ...m, followers_count: (m.followers_count || 0) + (data.following ? 1 : -1) }));
    toast.success(data.following ? t("following_success") : t("unfollowed"));
  };

  if (!assoc || !mosque) return <div className="min-h-screen bg-background" />;

  const mapUrl = mosque.lat && mosque.lng
    ? `https://www.openstreetmap.org/?mlat=${mosque.lat}&mlon=${mosque.lng}#map=16/${mosque.lat}/${mosque.lng}`
    : `https://www.openstreetmap.org/search?query=${encodeURIComponent(mosque.address)}`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar assoc={assoc} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="mosque-back-btn" onClick={() => navigate(`/${slug}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>

        {mosque.photo && (
          <div className="rounded-xl overflow-hidden border border-border mb-6">
            <img src={mosque.photo} alt={mosque.name} className="w-full h-64 object-cover" />
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-3" data-testid="mosque-name">
              <span className="h-4 w-4 rounded-full shrink-0" style={{ background: mosque.color }} />
              {mosque.name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1.5" data-testid="mosque-followers-count">
              <Heart className="h-3.5 w-3.5 text-red-500 fill-red-500" /> {mosque.followers_count || 0} {t((mosque.followers_count || 0) > 1 ? "mosque_members" : "mosque_member_singular")}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <InviteButton mosque={mosque} slug={slug} />
            <Button
              data-testid="mosque-follow-btn"
              onClick={toggleFollow}
              variant={following ? "secondary" : "default"}
              className="rounded-full px-6 active:scale-95 shrink-0"
            >
              {following ? <><Check className="h-4 w-4 mr-2" />{t("mosque_member")}</> : <><Heart className="h-4 w-4 mr-2" />{t("become_member")}</>}
            </Button>
          </div>
        </div>
        {mosque.description && <p className="text-muted-foreground mt-3 leading-relaxed">{mosque.description}</p>}

        <PrayerTimesRow slug={slug} mosqueId={mosqueId} title={t("prayer_times")} className="mt-8" />

        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          <div className="border border-border rounded-lg bg-card p-5 space-y-3">
            <p className="text-[11px] font-mono-data uppercase tracking-wider text-muted-foreground">{t("contact")}</p>
            {mosque.address && (
              <p className="text-sm flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-accent shrink-0" />{mosque.address}</p>
            )}
            {mosque.phone && <p className="text-sm flex items-center gap-2 font-mono-data"><Phone className="h-4 w-4 text-accent" />{mosque.phone}</p>}
            {mosque.email && <p className="text-sm flex items-center gap-2"><Mail className="h-4 w-4 text-accent" />{mosque.email}</p>}
            {mosque.website && (
              <a href={mosque.website} target="_blank" rel="noreferrer" className="text-sm flex items-center gap-2 text-accent hover:underline">
                <Globe className="h-4 w-4" />{mosque.website}
              </a>
            )}
            {(mosque.facebook || mosque.instagram || mosque.youtube) && (
              <div className="flex items-center gap-2 pt-1" data-testid="mosque-social-links">
                {mosque.facebook && (
                  <a href={mosque.facebook} target="_blank" rel="noreferrer" data-testid="mosque-facebook-link" aria-label="Facebook"
                     className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent transition-colors">
                    <Facebook className="h-4 w-4" />
                  </a>
                )}
                {mosque.instagram && (
                  <a href={mosque.instagram} target="_blank" rel="noreferrer" data-testid="mosque-instagram-link" aria-label="Instagram"
                     className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent transition-colors">
                    <Instagram className="h-4 w-4" />
                  </a>
                )}
                {mosque.youtube && (
                  <a href={mosque.youtube} target="_blank" rel="noreferrer" data-testid="mosque-youtube-link" aria-label="YouTube"
                     className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent transition-colors">
                    <Youtube className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
            <Button variant="outline" size="sm" data-testid="mosque-map-btn" onClick={() => window.open(mapUrl, "_blank")} className="rounded-full mt-2 active:scale-95">
              <ExternalLink className="h-3.5 w-3.5 mr-2" /> {t("open_map")}
            </Button>
          </div>
          <div className="border border-border rounded-lg bg-card p-5">
            <p className="text-[11px] font-mono-data uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{t("hours")}</p>
            <p className="text-sm mt-3 leading-relaxed">{mosque.hours || "—"}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-12 mb-5">
          <h2 className="font-heading font-bold text-lg">{t("upcoming_events")}</h2>
          <Button
            variant="outline"
            size="sm"
            data-testid="mosque-calendar-btn"
            onClick={() => navigate(`/${slug}/calendar?mosque=${mosqueId}`)}
            className="rounded-full active:scale-95"
          >
            <CalendarDays className="h-3.5 w-3.5 mr-2" /> {t("view_full_calendar")}
          </Button>
        </div>
        <div className="mb-4">
          <CategoryFilter events={events} value={cat} onChange={setCat} />
        </div>
        <div className="space-y-3" data-testid="mosque-events-list">
          {(cat === "all" ? events : events.filter((e) => (e.category || "other") === cat)).length === 0 && <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">{t("no_events")}</p>}
          {(cat === "all" ? events : events.filter((e) => (e.category || "other") === cat)).map((e) => (
            <EventCard key={e.id} event={e} slug={slug} hijriOffset={assoc.hijri_offset || 0} />
          ))}
        </div>
      </div>
    </div>
  );
}
